import { getList } from '@/requests/api/get-list';
import {
  type BrfApartment,
  type BrfApartmentInvoiceConfiguration,
  type RetroactiveBrfApartmentRevenueProposal,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';

export async function brfApartmentRevenueProposalNestedCase(
  listResponse: TCustomList<RetroactiveBrfApartmentRevenueProposal>
): Promise<TCustomList<RetroactiveBrfApartmentRevenueProposal>> {
  const invoicingContractSourceObjectIds = listResponse.data.reduce<
    Set<string>
  >((acc, retroactiveBrfApartmentRevenueProposal) => {
    if (retroactiveBrfApartmentRevenueProposal.targetContract?.id) {
      acc.add(retroactiveBrfApartmentRevenueProposal.targetContract.id);
    }
    return acc;
  }, new Set());

  if (invoicingContractSourceObjectIds.size === 0) return listResponse;

  const chunkedSourceIds = chunkIds(invoicingContractSourceObjectIds);

  const resolvedSourcesPromises = await Promise.allSettled(
    chunkedSourceIds.map((chunk) =>
      getList<BrfApartmentInvoiceConfiguration>({
        modelName: 'brfapartmentinvoiceconfiguration',
        queryParams: {
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedSourceObjects: BrfApartmentInvoiceConfiguration[] = [];

  const failedSourceObjects: unknown[] = [];
  for (const promise of resolvedSourcesPromises) {
    if (promise.status === 'fulfilled') {
      resolvedSourceObjects.push(...(promise.value.list ?? []));
    } else {
      failedSourceObjects.push(promise.reason);
    }
  }

  // then fetch and resolve the brf apartment for each contract

  const brfApartmentIds = resolvedSourceObjects.reduce<Set<string>>(
    (acc, brfApartmentInvoiceConfiguration) => {
      if (brfApartmentInvoiceConfiguration.brfApartment?.id) {
        acc.add(brfApartmentInvoiceConfiguration.brfApartment.id);
      }
      return acc;
    },
    new Set()
  );

  const chunkedBrfApartmentIds = chunkIds(brfApartmentIds);
  const resolvedBrfApartmentPromises = await Promise.allSettled(
    chunkedBrfApartmentIds.map((chunk) =>
      getList<BrfApartment>({
        modelName: 'brfapartment',
        queryParams: {
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedBrfApartments: BrfApartment[] = [];
  const failedBrfApartments: unknown[] = [];
  for (const promise of resolvedBrfApartmentPromises) {
    if (promise.status === 'fulfilled') {
      resolvedBrfApartments.push(...(promise.value.list ?? []));
    } else {
      failedBrfApartments.push(promise.reason);
    }
  }
  const response = {
    ...listResponse,
    data: listResponse.data.map((retroactiveBrfApartmentRevenueProposal) => {
      const sourceInvoiceConfigurationId =
        retroactiveBrfApartmentRevenueProposal.targetContract?.id;
      if (!sourceInvoiceConfigurationId)
        return retroactiveBrfApartmentRevenueProposal;

      const sourceInvoiceConfiguration = resolvedSourceObjects.find(
        (invoiceConfiguration) =>
          invoiceConfiguration.id === sourceInvoiceConfigurationId
      );
      if (!sourceInvoiceConfiguration)
        return retroactiveBrfApartmentRevenueProposal;

      const sourceBrfApartmentId = sourceInvoiceConfiguration.brfApartment?.id;
      if (!sourceBrfApartmentId) return retroactiveBrfApartmentRevenueProposal;

      const sourceBrfApartment = resolvedBrfApartments.find(
        (brfApartment) => brfApartment.id === sourceBrfApartmentId
      );
      if (!sourceBrfApartment) return retroactiveBrfApartmentRevenueProposal;

      return {
        ...retroactiveBrfApartmentRevenueProposal,
        targetContract: {
          ...sourceInvoiceConfiguration,
          brfApartment: sourceBrfApartment,
        },
      };
    }),
  };

  return response;
}
